import React, { useEffect, useRef, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import {
  onAuthChange, adminLogout, checkIsAdmin, getAdminRole, getAdminMfaStatus, ensureAdminClaims,
  listenDrivers, listenRides, listenUsers, listenSosEvents, listenDriverPayments, fetchDriverLastSeen,
} from './services/firebase';
import { getStuckRides } from './utils/rides';
import { countOnline } from './utils/onlineCounts';
import { hasDocPendingReview } from './utils/driverDocs';
import { useLang } from './lang/LangContext';
import Login from './pages/Login';
import AdminMfaRequired from './components/AdminMfaRequired';
import Dashboard from './pages/Dashboard';
import Rides from './pages/Rides';
import Drivers from './pages/Drivers';
import Passengers from './pages/Passengers';
import LiveMap from './pages/LiveMap';
import Analytics from './pages/Analytics';
import Cash from './pages/Cash';
import Settings from './pages/Settings';
import Activity from './pages/Activity';
import Broadcast from './pages/Broadcast';
import Dispatch from './pages/Dispatch';
import SosInbox from './pages/SosInbox';
import StuckRides from './pages/StuckRides';
import DeletionRequests from './pages/DeletionRequests';
import FlaggedRides from './pages/FlaggedRides';
import RoadTraces from './pages/RoadTraces';
import DemandHeatmap from './pages/DemandHeatmap';
import DriverLeaderboard from './pages/DriverLeaderboard';
import AdminAiCopilot from './pages/AdminAiCopilot';
import Intertown from './pages/Intertown';
import MapAds from './pages/MapAds';
import DispatchEngine from './pages/DispatchEngine';

const NAV = [
  { section: 'OVERVIEW' },
  { id: 'dashboard', label: 'Dashboard', icon: '⚡' },
  { id: 'ai-copilot', label: 'AI Super Tool', icon: '🤖' },
  { id: 'analytics', label: 'Analytics', icon: '📊' },
  { id: 'demand', label: 'Demand Heatmap', icon: '🔥' },
  { id: 'map', label: 'Live map', icon: '🗺️' },
  { id: 'road-traces', label: 'Road Network', icon: '🛣️' },
  { section: 'OPS' },
  { id: 'rides', label: 'Rides', icon: '🛺' },
  { id: 'drivers', label: 'Annas (Drivers)', icon: '👨‍✈️' },
  { id: 'passengers', label: 'Passengers', icon: '👥' },
  { id: 'leaderboard', label: 'Anna Leaderboard', icon: '🏆' },
  { id: 'flagged', label: 'Flagged / Issues', icon: '🚩' },
  { id: 'dispatch', label: 'Phone Dispatch', icon: '📞' },
  { id: 'dispatch-engine', label: 'Dispatch Engine', icon: '🧭' },
  { id: 'intertown', label: 'Intertown', icon: '🛣️' },
  { id: 'broadcast', label: 'Broadcast FCM', icon: '📢' },
  { id: 'map-ads', label: 'Map Ads', icon: '🏷️' },
  { id: 'sos', label: 'SOS inbox', icon: '🚨' },
  { id: 'stuck', label: 'Stuck rides', icon: '⏱️' },
  { id: 'deletions', label: 'Deletions', icon: '🗑️' },
  { id: 'cash', label: 'Cash today', icon: '💵' },
  { section: 'SYSTEM' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
  { id: 'activity', label: 'Activity', icon: '📋' },
];

const TITLES = {
  dashboard: 'Dashboard',
  'ai-copilot': 'Admin AI Super Tool (Fleet Intelligence & Copilot)',
  analytics: 'Analytics',
  demand: 'Passenger Demand & Missed Surge Heatmap',
  map: 'Live map',
  'road-traces': 'Piler Road Network & Trace Explorer',
  leaderboard: 'Anna Fleet Leaderboard & Weekly Champions',
  flagged: 'Flagged Rides & Safety Issues',
  dispatch: 'Manual Telephone Dispatcher',
  'dispatch-engine': 'Dispatch Engine',
  intertown: 'Intertown control centre',
  broadcast: 'Broadcast FCM Announcements',
  'map-ads': 'Map Ads',
  sos: 'SOS / emergency inbox',
  stuck: 'Stuck ride queue',
  rides: 'Rides',
  drivers: 'Annas Fleet (Drivers)',
  passengers: 'Passengers',
  deletions: 'Account deletion requests',
  cash: 'Cash today',
  settings: 'Settings',
  activity: 'Activity log',
};

// The panel has no URL router — the sidebar just flips this `page` state — so a
// browser refresh would otherwise always snap back to Dashboard. Persist the
// last page per-browser and restore it on load, validated against the real nav
// ids so a stale/garbage value can never render a blank body.
const PAGE_IDS = new Set(NAV.filter((item) => item.id).map((item) => item.id));
const LAST_PAGE_KEY = 'mt_admin_page';

function readLastPage() {
  try {
    const saved = localStorage.getItem(LAST_PAGE_KEY);
    if (saved && PAGE_IDS.has(saved)) return saved;
  } catch { /* storage blocked (private window) — fall through to default */ }
  return 'dashboard';
}

export default function App() {
  const { lang, setLang, t } = useLang();
  const [user, setUser] = useState(undefined);
  const [isAdmin, setIsAdmin] = useState(undefined);
  const [adminRole, setAdminRole] = useState(null);
  const [mfa, setMfa] = useState(null); // { enrolled, sessionMfa } | null while loading
  const [page, setPage] = useState(readLastPage);
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [users, setUsers] = useState([]);
  const [sosEvents, setSosEvents] = useState([]);
  const [driverPayments, setDriverPayments] = useState([]);
  // id -> when his GPS row was last written; null until the first read (or when it fails).
  const [lastSeenById, setLastSeenById] = useState(null);
  const [inspectRideId, setInspectRideId] = useState(null);
  const [driversFilter, setDriversFilter] = useState(null);

  const isSuperAdmin = adminRole === 'super_admin';

  const handleSelectRide = (r) => {
    setInspectRideId(r?.id || null);
    setPage('rides');
  };

  // Navigate to a page, optionally pre-setting a filter on that page.
  // navigate('drivers', { filter: 'docs_review' }) → land straight on the queue.
  const navigate = (target, opts) => {
    if (target === 'drivers') setDriversFilter(opts?.filter || null);
    setPage(target);
  };

  // Remember the open page so a refresh restores it (see readLastPage).
  useEffect(() => {
    try { localStorage.setItem(LAST_PAGE_KEY, page); } catch { /* storage blocked */ }
  }, [page]);

  useEffect(() => onAuthChange(async (authUser) => {
    setUser(authUser);
    setAdminRole(null);
    if (authUser) {
      const adminOk = await checkIsAdmin(authUser.uid);
      setIsAdmin(adminOk);
      const mfaStatus = await getAdminMfaStatus();
      setMfa(mfaStatus);
      if (adminOk) {
        const role = await getAdminRole(authUser.uid);
        setAdminRole(role || 'invalid');
      }
      if (adminOk && mfaStatus?.sessionMfa) {
        ensureAdminClaims().catch(() => {});
      }
    } else {
      setIsAdmin(undefined);
      setAdminRole(null);
      setMfa(null);
    }
  }), []);

  useEffect(() => {
    if (!isAdmin || !mfa?.sessionMfa) return undefined;
    const u1 = listenRides(setRides);
    const u2 = listenDrivers(setDrivers);
    const u3 = listenUsers(setUsers);
    const u4 = listenSosEvents(setSosEvents);
    const u5 = listenDriverPayments(setDriverPayments);
    return () => { u1?.(); u2?.(); u3?.(); u4?.(); u5?.(); };
  }, [isAdmin, mfa?.sessionMfa]);

  // The header's "live" count needs to know who is really sending GPS. A one-shot read once a
  // minute (skipped while the tab is hidden), not a stream: driverLocations changes about twice
  // a second and a listener would download every tick to every open admin tab.
  useEffect(() => {
    if (!isAdmin || !mfa?.sessionMfa) return undefined;
    let cancelled = false;
    const load = async () => {
      if (document.hidden) return;
      try {
        const map = await fetchDriverLastSeen();
        if (!cancelled) setLastSeenById(map);
      } catch {
        // Fall back to the single marked-online number rather than showing a wrong "0 live".
        if (!cancelled) setLastSeenById(null);
      }
    };
    load();
    const timer = setInterval(load, 60000);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAdmin, mfa?.sessionMfa]);

  // Nav badge = Annas that need a review action: awaiting first approval, OR
  // (already approved or not) sitting with a freshly uploaded/re-uploaded doc.
  const pendingDrivers = drivers.filter(
    (d) => (!d.isApproved && !d.isBlocked) || hasDocPendingReview(d),
  ).length;
  const searching = rides.filter((r) => r.status === 'searching').length;
  const openSos = sosEvents.filter((e) => !e.handlingStatus || e.handlingStatus === 'open').length;
  const stuckCount = getStuckRides(rides).length;

  useEffect(() => {
    if (openSos <= 0) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.4);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.5);
      osc.onended = () => {
        try { ctx.close(); } catch { /* ignore */ }
      };
    } catch { /* AudioContext interaction restriction */ }
  }, [openSos]);

  // A gentle ding when a new ride starts searching — deliberately distinct
  // from the SOS tone above (short sine chime vs. urgent descending
  // sawtooth) so an admin can tell them apart by ear. Unlike the SOS effect,
  // this only fires on a genuine increase (tracked via a ref), never on
  // mount/refresh — rides sit in 'searching' far more often than SOS events
  // exist, so beeping just because one happens to already be searching when
  // the page loads would be noisy rather than useful.
  const prevSearchingRef = useRef(searching);
  useEffect(() => {
    const prev = prevSearchingRef.current;
    prevSearchingRef.current = searching;
    if (searching <= prev) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1046, ctx.currentTime);
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.25);
      osc.onended = () => {
        try { ctx.close(); } catch { /* ignore */ }
      };
    } catch { /* AudioContext interaction restriction */ }
  }, [searching]);

  if (user === undefined || (user && (isAdmin === undefined || (isAdmin && mfa === null)))) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--bg)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
      >
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Login />
        <Toaster position="top-right" toastOptions={{ style: { background: '#141a22', color: '#e2e8f0', border: '1px solid #1e2730', borderRadius: 10 } }} />
      </>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--bg)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center',
      }}
      >
        <div>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🚫</div>
          <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 22, marginBottom: 8 }}>Not an admin</div>
          <p className="muted" style={{ marginBottom: 8 }}>Signed in as {user.email || user.uid}</p>
          <p className="muted" style={{ marginBottom: 20, maxWidth: 360 }}>
            Add a Firestore document at <code>admins/{user.uid}</code> (or set custom claim admin=true), then refresh.
          </p>
          <button type="button" className="btn btn-ghost" onClick={() => adminLogout()}>Sign out</button>
        </div>
        <Toaster position="top-right" />
      </div>
    );
  }

  // Firebase Auth MFA is mandatory for admin. Block the panel until the account
  // has a second factor AND this sign-in actually completed it (token claim
  // firebase.sign_in_second_factor). No sessionStorage gate.
  if (!mfa?.sessionMfa) {
    return (
      <>
        <AdminMfaRequired
          user={user}
          needsEnrollment={!mfa?.enrolled}
          emailVerified={!!mfa?.emailVerified}
          onEnrolled={async () => setMfa(await getAdminMfaStatus())}
        />
        <Toaster position="top-right" toastOptions={{ style: { background: 'rgba(15, 23, 42, 0.95)', color: '#f8fafc', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 12 } }} />
      </>
    );
  }

  // marked = switched on in the app; live = also sending GPS in the last 3 minutes (the rest are
  // "zombie online": phones that went quiet while still marked online).
  const { marked: onlineCaptainsCount, live: liveCaptainsCount } = countOnline({ drivers, lastSeenById });

  return (
    <div className="admin-layout">
      <aside className="sidebar" style={lang === 'te' ? { width: 260 } : undefined}>
        <div className="sidebar-logo">
          <img src="/icons/logo-wordmark.png" alt="MyTownly" style={{ height: 36, width: 'auto', objectFit: 'contain' }} />
          <sub>{t('COMMAND CENTER')}</sub>
        </div>
        {NAV.map((item) => (
          item.section ? (
            <div key={item.section} className="nav-section">{t(item.section)}</div>
          ) : (
            <button
              key={item.id}
              type="button"
              className={`nav-item${page === item.id ? ' active' : ''}`}
              onClick={() => navigate(item.id)}
            >
              <span className="icon">{item.icon}</span>
              {t(item.label)}
              {item.id === 'drivers' && pendingDrivers > 0 && (
                <span className="nav-badge">{pendingDrivers}</span>
              )}
              {item.id === 'rides' && searching > 0 && (
                <span className="nav-badge">{searching}</span>
              )}
              {item.id === 'sos' && openSos > 0 && (
                <span className="nav-badge" style={{ animation: 'pulseDot 1.5s infinite' }}>{openSos}</span>
              )}
              {item.id === 'stuck' && stuckCount > 0 && (
                <span className="nav-badge">{stuckCount}</span>
              )}
            </button>
          )
        ))}
        <div className="sidebar-footer">
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 8,
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            fontFamily: 'JetBrains Mono, monospace',
            background: isSuperAdmin ? 'rgba(0, 168, 107, 0.15)' : 'rgba(56, 189, 248, 0.15)',
            color: isSuperAdmin ? '#34d399' : '#38bdf8',
            border: `1px solid ${isSuperAdmin ? 'rgba(0, 168, 107, 0.3)' : 'rgba(56, 189, 248, 0.3)'}`,
            marginBottom: 8,
          }}>
            <span>{isSuperAdmin ? '👑' : '🔍'}</span>
            <span>{isSuperAdmin ? t('Super Admin') : t('Reviewer (Read+KYC)')}</span>
          </div>
          <div className="muted" style={{ marginBottom: 8, fontSize: '11.5px', wordBreak: 'break-all' }}>{user.email}</div>
          {/* Language toggle */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button
              type="button"
              onClick={() => setLang('en')}
              style={{
                flex: 1, padding: '5px 0', borderRadius: 6, border: '1px solid',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                background: lang === 'en' ? 'var(--green)' : 'transparent',
                color: lang === 'en' ? '#04140c' : 'var(--text2)',
                borderColor: lang === 'en' ? 'var(--green)' : 'var(--border2)',
              }}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLang('te')}
              style={{
                flex: 1, padding: '5px 0', borderRadius: 6, border: '1px solid',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                background: lang === 'te' ? 'var(--green)' : 'transparent',
                color: lang === 'te' ? '#04140c' : 'var(--text2)',
                borderColor: lang === 'te' ? 'var(--green)' : 'var(--border2)',
              }}
            >
              తె
            </button>
          </div>
          <button type="button" className="btn btn-ghost" style={{ width: '100%' }} onClick={() => adminLogout()}>
            {t('Sign out')}
          </button>
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <div>
            <div className="topbar-title">{t(TITLES[page])}</div>
          </div>
          <div className="topbar-spacer" />
          
          {/* Live Operations Status Pills */}
          <div className="topbar-status-pill">
            <span className="topbar-dot-pulse" />
            <span>Pileru (AP)</span>
          </div>

          <div
            className="topbar-status-pill"
            style={{ cursor: 'pointer' }}
            onClick={() => setPage('drivers')}
          >
            <span>🛺</span>
            <span
              style={{ color: (liveCaptainsCount == null ? onlineCaptainsCount : liveCaptainsCount) > 0 ? '#34d399' : '#94a3b8' }}
              title={liveCaptainsCount == null ? undefined : t('Live = sending GPS in the last 3 minutes. Marked online = switched on in the app, including phones that have gone quiet.')}
            >
              {liveCaptainsCount == null
                ? <>{onlineCaptainsCount} {t('Annas Online')}</>
                : <>{liveCaptainsCount} {t('live')} · {onlineCaptainsCount} {t('marked online')}</>}
            </span>
          </div>

          {openSos > 0 && (
            <button
              type="button"
              className="btn btn-red btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', animation: 'pulseDot 1.5s infinite' }}
              onClick={() => setPage('sos')}
            >
              <span>🚨</span>
              <span>{openSos} {t('SOS ALARM')}</span>
            </button>
          )}
        </div>
        <div className="page-body">
          {page === 'dashboard' && (
            <Dashboard
              rides={rides}
              drivers={drivers}
              users={users}
              openSos={openSos}
              stuckCount={stuckCount}
              onNavigate={navigate}
            />
          )}
          {page === 'ai-copilot' && <AdminAiCopilot drivers={drivers} rides={rides} users={users} isSuperAdmin={isSuperAdmin} />}
          {page === 'flagged' && <FlaggedRides rides={rides} users={users} drivers={drivers} onSelectRide={handleSelectRide} isSuperAdmin={isSuperAdmin} />}
          {page === 'dispatch' && <Dispatch drivers={drivers} rides={rides} onNavigate={navigate} isSuperAdmin={isSuperAdmin} />}
          {page === 'dispatch-engine' && <DispatchEngine rides={rides} isSuperAdmin={isSuperAdmin} />}
          {page === 'intertown' && <Intertown drivers={drivers} isSuperAdmin={isSuperAdmin} />}
          {page === 'broadcast' && <Broadcast drivers={drivers} users={users} isSuperAdmin={isSuperAdmin} />}
          {page === 'map-ads' && <MapAds isSuperAdmin={isSuperAdmin} />}
          {page === 'analytics' && <Analytics rides={rides} drivers={drivers} />}
          {page === 'demand' && <DemandHeatmap rides={rides} />}
          {page === 'map' && <LiveMap drivers={drivers} rides={rides} />}
          {page === 'road-traces' && <RoadTraces drivers={drivers} rides={rides} />}
          {page === 'leaderboard' && <DriverLeaderboard drivers={drivers} rides={rides} />}
          {page === 'sos' && <SosInbox rides={rides} users={users} onSelectRide={handleSelectRide} isSuperAdmin={isSuperAdmin} />}
          {page === 'stuck' && <StuckRides rides={rides} onSelectRide={handleSelectRide} isSuperAdmin={isSuperAdmin} />}
          {page === 'deletions' && <DeletionRequests isSuperAdmin={isSuperAdmin} />}
          {page === 'rides' && (
            <Rides
              rides={rides}
              drivers={drivers}
              initialSelectedId={inspectRideId}
              onClearSelected={() => setInspectRideId(null)}
              isSuperAdmin={isSuperAdmin}
            />
          )}
          {page === 'drivers' && <Drivers drivers={drivers} rides={rides} initialFilter={driversFilter} isSuperAdmin={isSuperAdmin} />}
          {page === 'passengers' && <Passengers users={users} rides={rides} isSuperAdmin={isSuperAdmin} />}
          {page === 'cash' && <Cash rides={rides} drivers={drivers} driverPayments={driverPayments} isSuperAdmin={isSuperAdmin} />}
          {page === 'settings' && <Settings isSuperAdmin={isSuperAdmin} />}
          {page === 'activity' && <Activity />}
        </div>
      </main>

      <Toaster position="top-right" toastOptions={{ style: { background: 'rgba(15, 23, 42, 0.95)', color: '#f8fafc', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 12, backdropFilter: 'blur(16px)' } }} />
    </div>
  );
}
